/*
 * GameKit — what every single-player puzzle on the site needs the same way.
 *
 *   GameKit.rng(seed)             reproducible random source: a deal or a puzzle IS its number
 *   GameKit.hash(text)            text → uint32 seed
 *   GameKit.dailyKey([date])      the player's calendar day, 'YYYY-MM-DD'
 *   GameKit.shiftDay(key, n)      the day n days away
 *   GameKit.dailySeed(key, salt)  the seed of the "puzzle of the day"
 *   GameKit.clock(opts)           play time: stands still while the board is off screen
 *   GameKit.formatTime(ms, opts)  m:ss / h:mm:ss, optionally with tenths
 *   GameKit.stats(slug, opts)     played / won / best / streaks per variant, plus the daily log
 *   GameKit.share                 compact codec for "play my board" links
 *   GameKit.immersive(el, opts)   the board takes the whole screen: real fullscreen, else an overlay
 *
 * A classic script on purpose: the page loads it with a tag, a worker with
 * importScripts, node tests through vm — one file, three hosts. The generator
 * uses integer math only, so one seed gives the same board in every engine.
 * Why each piece is shaped the way it is — docs/why/primitives/game-kit.md.
 */
(function (root) {
    'use strict';

    // ── Seeds ────────────────────────────────────────────────────────────────
    // FNV-1a over UTF-16 units with a murmur3 finisher: short, similar strings
    // ("sudoku|2026-10-14" / "sudoku|2026-10-15") must land far apart.
    function hash(text) {
        text = String(text);
        var h = 0x811c9dc5;
        for (var i = 0; i < text.length; i++) {
            h ^= text.charCodeAt(i);
            h = Math.imul(h, 0x01000193);
        }
        h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
        h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
        h ^= h >>> 16;
        return h >>> 0;
    }

    // sfc32, its state filled by splitmix32. Never Math.random and never a
    // float in the state: a shared link or a daily puzzle has to rebuild the
    // very same board on another device.
    function rng(seed) {
        var s = (typeof seed === 'string') ? hash(seed) : (seed >>> 0);
        function fill() {
            s = (s + 0x9e3779b9) | 0;
            var z = s;
            z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
            z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
            return (z ^ (z >>> 15)) >>> 0;
        }
        var a = fill(), b = fill(), c = fill(), d = 1;
        function u32() {
            var t = (a + b) | 0;
            t = (t + d) | 0;
            d = (d + 1) | 0;
            a = b ^ (b >>> 9);
            b = (c + (c << 3)) | 0;
            c = (c << 21) | (c >>> 11);
            c = (c + t) | 0;
            return t >>> 0;
        }
        for (var i = 0; i < 12; i++) u32();
        var api = {
            u32: u32,
            next: function () { return u32() / 4294967296; },
            // 0 … n-1 without modulo bias: a biased shuffle makes some deals
            // likelier than others, and a deal number stops being a fair draw.
            int: function (n) {
                n = Math.floor(n);
                if (!(n > 1)) return 0;
                var limit = 4294967296 - (4294967296 % n), x;
                do { x = u32(); } while (x >= limit);
                return x % n;
            },
            range: function (lo, hi) { return lo + api.int(hi - lo + 1); },
            chance: function (p) { return api.next() < p; },
            pick: function (list) { return list[api.int(list.length)]; },
            shuffle: function (list) {
                for (var i = list.length - 1; i > 0; i--) {
                    var j = api.int(i + 1), t = list[i];
                    list[i] = list[j];
                    list[j] = t;
                }
                return list;
            },
            // An independent stream for a sub-task (layout, decoration), so
            // adding a draw there does not shift the board itself.
            fork: function (label) {
                return rng((u32() ^ hash(label == null ? '' : label)) >>> 0);
            }
        };
        return api;
    }

    // ── The day ──────────────────────────────────────────────────────────────
    // The player's own calendar day: "today's puzzle" changes at their
    // midnight, not at UTC midnight in the middle of their evening.
    function pad2(n) { return (n < 10 ? '0' : '') + n; }
    function dailyKey(date) {
        var d = (date instanceof Date) ? date : new Date(date == null ? Date.now() : date);
        return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
    }
    function shiftDay(key, delta) {
        var p = String(key).split('-');
        return dailyKey(new Date(+p[0], +p[1] - 1, +p[2] + (delta | 0)));
    }
    function dailySeed(key, salt) { return hash(String(salt == null ? '' : salt) + '|' + String(key)); }

    // ── Play clock ───────────────────────────────────────────────────────────
    // Counts the time the board could be seen. A hidden tab, a locked phone or
    // a switched app suspend it; coming back resumes it without a click.
    //   start()    run (or keep suspended until the page is visible)
    //   pause()    the game itself stops the clock
    //   reset(ms)  stop and set the reading — 0 for a new game, saved ms for a restored one
    //   elapsed()  whole milliseconds
    function clock(opts) {
        opts = opts || {};
        var doc = (opts.document === undefined) ? root.document : opts.document;
        var now = opts.now || function () {
            return (root.performance && typeof root.performance.now === 'function') ? root.performance.now() : Date.now();
        };
        var watchHidden = opts.pauseWhenHidden !== false;
        var tickMs = opts.tickMs || 250;
        var base = 0, since = null, wanted = false, timer = null;

        function reading() { return base + (since === null ? 0 : now() - since); }
        function tick() {
            if (typeof opts.onTick !== 'function') return;
            try { opts.onTick(Math.round(reading())); } catch (_) {}
        }
        function run() {
            if (since !== null) return;
            since = now();
            if (!timer && typeof root.setInterval === 'function') timer = root.setInterval(tick, tickMs);
        }
        function halt() {
            if (since !== null) { base += now() - since; since = null; }
            if (timer) { root.clearInterval(timer); timer = null; }
        }
        function hidden() { return !!(watchHidden && doc && doc.visibilityState === 'hidden'); }
        function onVisibility() {
            if (!wanted) return;
            if (hidden()) halt(); else run();
            tick();
        }
        if (watchHidden && doc && typeof doc.addEventListener === 'function') {
            doc.addEventListener('visibilitychange', onVisibility);
        }
        return {
            start: function () { wanted = true; if (!hidden()) run(); tick(); },
            pause: function () { wanted = false; halt(); tick(); },
            reset: function (ms) { wanted = false; halt(); base = Math.max(0, +ms || 0); tick(); },
            elapsed: function () { return Math.round(reading()); },
            running: function () { return wanted; },
            destroy: function () {
                wanted = false; halt();
                if (watchHidden && doc && typeof doc.removeEventListener === 'function') {
                    doc.removeEventListener('visibilitychange', onVisibility);
                }
            }
        };
    }

    function formatTime(ms, opts) {
        opts = opts || {};
        ms = Math.max(0, +ms || 0);
        var total = Math.floor(ms / 1000);
        var h = Math.floor(total / 3600), m = Math.floor((total % 3600) / 60), s = total % 60;
        var out = (h > 0 ? h + ':' + pad2(m) : String(m)) + ':' + pad2(s);
        if (opts.tenths) out += '.' + Math.floor((ms % 1000) / 100);
        return out;
    }

    // ── Statistics ───────────────────────────────────────────────────────────
    // One record per finished game. A game finished with help (`assisted`:
    // a hint, a move taken back after losing) is still a game played and won,
    // but it sets no record — otherwise the best time is the hint's, not the
    // player's.
    //   record({variant, won, ms, score, lowerScoreWins, assisted, daily, at})
    //   summary(variant) · variants() · dailyResult(key, variant) · dailyStreak(today, variant) · reset(variant)
    function stats(slug, opts) {
        opts = opts || {};
        var KEY = 'gk_stats';
        var HISTORY = opts.history || 60;
        var DAYS = opts.days || 400;
        var memory = null;

        function store() {
            if (opts.storage) return opts.storage;
            var ts = root.__toolSettings;
            return (ts && typeof ts.save === 'function' && typeof ts.load === 'function') ? ts : null;
        }
        function read() {
            var st = store(), data = null;
            try { data = st ? st.load(slug, KEY, null) : memory; } catch (_) { data = null; }
            if (!data || typeof data !== 'object' || data.v !== 1 || typeof data.variants !== 'object' || !data.variants) {
                data = { v: 1, variants: {}, daily: {} };
            }
            if (typeof data.daily !== 'object' || !data.daily) data.daily = {};
            return data;
        }
        function write(data) {
            var st = store();
            if (st) { try { st.save(slug, KEY, data); } catch (_) {} } else memory = data;
        }
        function blank() {
            return { played: 0, won: 0, streak: 0, bestStreak: 0, bestMs: null, bestScore: null, sumMs: 0, timed: 0, last: [] };
        }
        function view(v) {
            v = v || blank();
            return {
                played: v.played, won: v.won, lost: v.played - v.won,
                winRate: v.played ? v.won / v.played : null,
                streak: v.streak, bestStreak: v.bestStreak,
                bestMs: v.bestMs, avgMs: v.timed ? Math.round(v.sumMs / v.timed) : null,
                bestScore: v.bestScore,
                last: (v.last || []).slice()
            };
        }
        function num(x) { return (typeof x === 'number' && isFinite(x)) ? x : null; }

        return {
            record: function (r) {
                r = r || {};
                var data = read();
                var id = String(r.variant == null ? 'default' : r.variant);
                var v = data.variants[id] || (data.variants[id] = blank());
                var won = !!r.won, assisted = !!r.assisted, clean = won && !assisted;
                var ms = num(r.ms), score = num(r.score);
                if (ms !== null) ms = Math.max(0, Math.round(ms));
                var out = { bestTime: false, bestScore: false };

                v.played++;
                if (won) {
                    v.won++; v.streak++;
                    if (v.streak > v.bestStreak) v.bestStreak = v.streak;
                } else {
                    v.streak = 0;
                }
                if (clean && ms !== null) {
                    v.sumMs += ms; v.timed++;
                    if (v.bestMs === null || ms < v.bestMs) { v.bestMs = ms; out.bestTime = true; }
                }
                if (clean && score !== null) {
                    var better = v.bestScore === null || (r.lowerScoreWins ? score < v.bestScore : score > v.bestScore);
                    if (better) { v.bestScore = score; out.bestScore = true; }
                }
                v.last.push({ t: num(r.at) || Date.now(), w: won ? 1 : 0, ms: ms, a: assisted ? 1 : 0, s: score });
                if (v.last.length > HISTORY) v.last.splice(0, v.last.length - HISTORY);

                if (r.daily && won) {
                    var dk = String(r.daily);
                    var day = data.daily[dk] || (data.daily[dk] = {});
                    var had = day[id];
                    var faster = had && clean && ms !== null && (had.a === 1 || had.ms === null || ms < had.ms);
                    if (!had || faster) day[id] = { ms: ms, a: assisted ? 1 : 0 };
                    var keys = Object.keys(data.daily).sort();
                    while (keys.length > DAYS) delete data.daily[keys.shift()];
                }
                write(data);
                out.summary = view(v);
                return out;
            },
            summary: function (variant) {
                return view(read().variants[String(variant == null ? 'default' : variant)]);
            },
            variants: function () { return Object.keys(read().variants); },
            dailyResult: function (key, variant) {
                var day = read().daily[String(key)];
                if (!day) return null;
                if (variant != null) return day[String(variant)] || null;
                var ids = Object.keys(day);
                return ids.length ? day[ids[0]] : null;
            },
            // Days in a row ending today — or yesterday, so the streak is not
            // shown as lost before today's puzzle has been played.
            dailyStreak: function (today, variant) {
                var daily = read().daily;
                function done(k) {
                    var day = daily[k];
                    if (!day) return false;
                    return variant != null ? !!day[String(variant)] : Object.keys(day).length > 0;
                }
                var k = String(today || dailyKey());
                if (!done(k)) k = shiftDay(k, -1);
                var n = 0;
                while (done(k)) { n++; k = shiftDay(k, -1); }
                return n;
            },
            reset: function (variant) {
                var data = read();
                if (variant == null) data = { v: 1, variants: {}, daily: {} };
                else delete data.variants[String(variant)];
                write(data);
            }
        };
    }

    // ── Links ────────────────────────────────────────────────────────────────
    // base64url without padding: safe in a query string and in a hash, and it
    // survives messengers that re-encode "+" and "=". Own tables instead of
    // btoa/atob — a worker and a node test have the same codec as the page.
    var B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
    var B64_REV = null;
    function toBase64Url(bytes) {
        var out = '', i = 0, n = bytes.length;
        for (; i + 2 < n; i += 3) {
            var x = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
            out += B64[x >>> 18] + B64[(x >>> 12) & 63] + B64[(x >>> 6) & 63] + B64[x & 63];
        }
        if (n - i === 1) {
            var y = bytes[i] << 16;
            out += B64[y >>> 18] + B64[(y >>> 12) & 63];
        } else if (n - i === 2) {
            var z = (bytes[i] << 16) | (bytes[i + 1] << 8);
            out += B64[z >>> 18] + B64[(z >>> 12) & 63] + B64[(z >>> 6) & 63];
        }
        return out;
    }
    function fromBase64Url(text) {
        text = String(text == null ? '' : text).replace(/=+$/, '');
        if (!B64_REV) { B64_REV = {}; for (var k = 0; k < 64; k++) B64_REV[B64[k]] = k; }
        if (text.length % 4 === 1) return null;
        var out = new Uint8Array(Math.floor(text.length * 3 / 4)), o = 0, acc = 0, bits = 0;
        for (var i = 0; i < text.length; i++) {
            var v = B64_REV[text[i]];
            if (v === undefined) return null;
            acc = (acc << 6) | v; bits += 6;
            if (bits >= 8) { bits -= 8; out[o++] = (acc >>> bits) & 255; }
        }
        return out;
    }
    function utf8(text) {
        if (typeof root.TextEncoder === 'function') return new root.TextEncoder().encode(text);
        var esc = unescape(encodeURIComponent(text)), out = new Uint8Array(esc.length);
        for (var i = 0; i < esc.length; i++) out[i] = esc.charCodeAt(i);
        return out;
    }
    function fromUtf8(bytes) {
        if (typeof root.TextDecoder === 'function') return new root.TextDecoder('utf-8', { fatal: true }).decode(bytes);
        var s = '';
        for (var i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
        return decodeURIComponent(escape(s));
    }
    var share = {
        toBase64Url: toBase64Url,
        fromBase64Url: fromBase64Url,
        encode: function (value) { return toBase64Url(utf8(JSON.stringify(value))); },
        // A link is typed, pasted, truncated by a messenger: anything that is
        // not a whole value comes back as null, never as a throw.
        decode: function (text) {
            try {
                var bytes = fromBase64Url(text);
                if (!bytes || !bytes.length) return null;
                var value = JSON.parse(fromUtf8(bytes));
                return value === undefined ? null : value;
            } catch (_) { return null; }
        }
    };

    // ── The board takes the screen ───────────────────────────────────────────
    // The site's own door, `window.__wholeScreen` (timbrica-core.js): real
    // fullscreen where the browser gives it to an element, otherwise the same
    // element as an overlay over the whole viewport — one button, one outcome.
    // The kit only names the hold and mirrors two class names the games style
    // against: `gk-full` in both modes, `gk-immersive` in the overlay.
    //   enter() → Promise<'fullscreen' | 'overlay'> · exit() · toggle() · active()
    function immersive(el, opts) {
        opts = opts || {};
        return root.__wholeScreen(el, {
            key: opts.key || 'game',
            overlayOnly: opts.overlayOnly,
            onChange: opts.onChange,
            classes: { full: 'gk-full', overlay: 'gk-immersive' }
        });
    }

    root.GameKit = {
        hash: hash,
        rng: rng,
        dailyKey: dailyKey,
        shiftDay: shiftDay,
        dailySeed: dailySeed,
        clock: clock,
        formatTime: formatTime,
        stats: stats,
        share: share,
        immersive: immersive
    };
})(typeof self !== 'undefined' ? self : (typeof globalThis !== 'undefined' ? globalThis : this));
